import React, { FC } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form/dist/index.ie11';

import { FieldMessage } from '@blueprint/ui';

import registrationAndSignInTranslations from 'public/locales/en/registration-and-sign-in.json';
import { getTranslator } from 'shared/i18n/getTranslator';

import {
    FormSection,
    ReassuranceText,
    RegisterButton
} from './../AuthModal.styles';
import { RadioGroup, RadioButton } from 'shared/components';
import PreferencesFormData from './../interfaces/PreferencesFormData';
import useUpdatePreferences from '../hooks/useUpdatePreferences';
import triggerTracking from '@blueprint/tracking';
import { useUser } from 'shared/hooks/useUser';
import { trackAuthSegmentEvent } from '../utils/segmentTrack';
import { PreferencesProps } from './Preferences.types';

const translate = getTranslator(registrationAndSignInTranslations);
const serviceError = translate('serviceError');
const termsUnsubscribe = translate('terms.youMayUnsubscribe');
const saveButton = translate('saveButton');

const PreferencesForm: FC<PreferencesProps> = ({
    close,
    actionAfterAuth,
    isSSOPhaseTwoFeatureEnabled
}) => {
    const {
        register,
        handleSubmit,
        errors,
        trigger
    } = useForm<PreferencesFormData>({
        mode: 'onBlur',
        reValidateMode: 'onBlur'
    });
    const user = useUser();

    const { updatePreferences, generalError } = useUpdatePreferences(close);

    const onFormSubmit: SubmitHandler<PreferencesFormData> = async (
        formData
    ) => {
        const payload = await updatePreferences(formData);
        const { consent__insights__24 } = formData;
        if (!payload?.data?.updateUserConsents) {
            return;
        }

        if (consent__insights__24 !== undefined) {
            triggerTracking({
                event: 'GAEvent',
                eventCategory: 'permission opt-in',
                eventAction: consent__insights__24
                    ? 'optin_both'
                    : 'optin_neither',
                eventLabel: `${
                    actionAfterAuth?.eventLabel || 'auth_modal'
                }_step_2`
            });

            if (consent__insights__24) {
                trackAuthSegmentEvent('optin', actionAfterAuth?.segment, user);
            }
        }

        triggerTracking({
            event: 'GAEvent',
            eventCategory: 'registration',
            eventAction: 'submit',
            eventLabel: `${actionAfterAuth?.eventLabel || 'auth_modal'}_step_2`
        });

        close();
    };

    const onSubmit = handleSubmit(onFormSubmit);

    return (
        <>
            <form
                onSubmit={onSubmit}
                noValidate
                data-testid="auth-modal-preferences-form"
            >
                {generalError && (
                    <FieldMessage
                        data-testid="general-error"
                        variant="invalid"
                        message={serviceError}
                    />
                )}

                {/* TODO: remove this placeholder with CRME-999 */}
                {isSSOPhaseTwoFeatureEnabled ? (
                    <FormSection>
                        <p>What can Zoopla help you with?</p>
                    </FormSection>
                ) : null}
                {/* end placeholder */}

                <FormSection>
                    <RadioGroup
                        legend={translate('emailPreferences.legend')}
                        description={translate('emailPreferences.description')}
                        radioGroupName="consent__insights__24"
                        errorMessage={
                            errors.consent__insights__24 &&
                            translate('emailPreferences.errorMessage')
                        }
                        onClick={() => trigger('consent__insights__24')}
                    >
                        <RadioButton
                            value="true"
                            id="yes"
                            activeTextColor="white"
                            label={translate('emailPreferences.yes')}
                            ref={register({ required: true })}
                        />
                        <RadioButton
                            value=""
                            id="no"
                            label={translate('emailPreferences.no')}
                            activeTextColor="white"
                            activeBackgroundColor="function-error"
                            ref={register({ required: true })}
                        />
                    </RadioGroup>
                </FormSection>
                <ReassuranceText
                    size="secondary"
                    color="grey-slate"
                    isBold
                    data-testid="unsubscribe-text"
                >
                    {termsUnsubscribe}
                </ReassuranceText>

                <RegisterButton
                    variant="primary"
                    isFullWidth
                    data-testid="update-preferences-button"
                >
                    {saveButton}
                </RegisterButton>
            </form>
        </>
    );
};

export default PreferencesForm;
